import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Stack,
  Checkbox,
} from "@mui/material";
import { fNumber } from "src/utils/formatNumber";
import { getCardAvgFpts } from "./utils";
import { getPositionText, getCardStyling } from "src/utils/cardStylingUtils";

/**
 * Reusable player card component
 * @param {Object} props
 * @param {Object} props.card - Card/player data
 * @param {string} props.variant - 'lineup' for PlayerLineup style, 'detailed' for UserCardsDialog style
 * @param {number} props.width - Card width (default: 120 for lineup, auto for detailed)
 * @param {Function} props.onClick - Optional click handler
 * @param {boolean} props.isSelectionMode - Whether the card is in selection mode
 * @param {boolean} props.isSelected - Whether the card is selected
 * @param {Function} props.onSelect - Selection handler function
 */
export default function PlayerCard({
  card,
  variant = "lineup",
  width,
  onClick,
  isSelectionMode = false,
  isSelected = false,
  onSelect,
}) {
  if (variant === "lineup") {
    return (
      <Card
        onClick={onClick}
        sx={{
          width: width || 120,
          flexShrink: 0,
          ...getCardStyling(card.cardRating),
          transition: "transform 0.2s",
          cursor: onClick ? "pointer" : "default",
          "&:hover": {
            transform: onClick ? "scale(1.05)" : "scale(1.02)",
          },
        }}
      >
        <Box sx={{ position: "relative" }}>
          <CardMedia
            component="img"
            height="120"
            image={
              card.teamExternalRef
                ? `https://assets.nhle.com/mugs/nhl/latest/${card.externalRef}.png`
                : `https://assets.nhle.com/logos/nhl/svg/${card.externalRef}_light.svg`
            }
            alt={card.title || card.playerName || card.knownname}
            sx={{ objectFit: "cover" }}
          />
          {/* Position circle in top left corner */}
          <Box
            sx={{
              position: "absolute",
              top: 5,
              left: 5,
              width: 28,
              height: 28,
              borderRadius: "50%",
              bgcolor: "rgba(0, 0, 0, 0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "0.65rem",
              fontWeight: "bold",
              color: "white",
              border: "1px solid rgba(255, 255, 255, 0.5)",
              boxShadow: "0 0 4px rgba(0, 0, 0, 0.5)",
              zIndex: 1,
            }}
          >
            {getPositionText(card.position)}
          </Box>
        </Box>
        <CardContent sx={{ p: 1, backgroundColor: "#272727" }}>
          <Typography
            variant="caption"
            component="div"
            noWrap
            sx={{ fontWeight: "bold" }}
          >
            {card.knownname || card.playerName || card.title}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.6rem",
              opacity: 0.8,
            }}
            noWrap
          >
            {card.jerseyNumber && `${card.jerseyNumber} - `}
            {card.team || card.teamName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.6rem",
              opacity: 0.8,
            }}
            component="div"
            noWrap
          >
            Avg: {getCardAvgFpts(card)}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      onClick={isSelectionMode ? () => onSelect?.(card) : onClick}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.2s, box-shadow 0.2s",
        cursor: isSelectionMode || onClick ? "pointer" : "default",
        ...getCardStyling(card.cardRating || 0),
        ...(isSelected && {
          borderColor: "primary",
          borderWidth: "3px",
          borderStyle: "solid",
          boxShadow: "0 0 12px rgba(25, 118, 210, 0.4)",
        }),
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: isSelected ? "0 0 16px rgba(25, 118, 210, 0.6)" : 4,
        },
      }}
    >
      <Box sx={{ position: "relative" }}>
        {isSelectionMode && (
          <Checkbox
            checked={isSelected}
            onChange={() => onSelect?.(card)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 2,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 1)",
              },
            }}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <CardMedia
          component="img"
          height="120"
          image={
            card.teamExternalRef
              ? `https://assets.nhle.com/mugs/nhl/latest/${card.externalRef}.png`
              : card.externalRef
              ? `https://assets.nhle.com/logos/nhl/svg/${card.externalRef}_light.svg`
              : `https://assets.nhle.com/mugs/nhl/latest/default.png`
          }
          alt={card.playerName || card.knownname || card.title}
          sx={{ objectFit: "cover" }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 5,
            left: 5,
            width: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontSize: "0.65rem",
            fontWeight: "bold",
            color: "white",
            border: "1px solid rgba(255, 255, 255, 0.5)",
            boxShadow: "0 0 4px rgba(0, 0, 0, 0.5)",
            zIndex: 1,
          }}
        >
          {getPositionText(card.playerPosition || card.position)}
        </Box>
      </Box>
      <CardContent sx={{ flexGrow: 1, p: 1.5, backgroundColor: "#272727" }}>
        <Stack spacing={1}>
          <Typography
            variant="subtitle2"
            noWrap
            title={card.playerName || card.knownname}
            sx={{ fontWeight: "bold", color: "white" }}
          >
            {card.playerName || card.knownname || "Unknown Player"}
          </Typography>

          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
            {card.teamName || card.team || "Unknown Team"}
          </Typography>

          <Box sx={{ mt: 1 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mb: 0.5 }}
            >
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                FPTS
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ color: "white" }}
              >
                {fNumber(
                  card.fpts || card.averageFpts || getCardAvgFpts(card) || 0
                )}
              </Typography>
            </Stack>

            {card.marketValue && (
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mb: 0.5 }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.7)" }}
                >
                  Market Value
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ color: "white" }}
                >
                  {fNumber(card.marketValue)}
                </Typography>
              </Stack>
            )}

            {card.level && (
              <Stack direction="row" justifyContent="space-between">
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.7)" }}
                >
                  Level
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ color: "white" }}
                >
                  {card.level}
                </Typography>
              </Stack>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
